import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, EyeOff, Save } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";

export const ApprovalEditor = ({ meetingId, race, analysis, onSaved, locked }) => {
  const api = useApi();
  const [sel, setSel] = useState([]);
  const [rec, setRec] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(analysis.selections || []); setRec(analysis.recommendation || ""); }, [analysis]);

  const picks = analysis.draft?.picks || {};
  const roleOf = (n) => (picks.top_pick?.number === n ? "top pick" : picks.danger?.number === n ? "danger" : picks.roughie?.number === n ? "roughie" : null);
  const add = () => {
    const r = race.runners.find((x) => String(x.number) === pick);
    if (r && !sel.some((s) => s.number === r.number)) setSel([...sel, { number: r.number, horse: r.horse, note: "", role: roleOf(r.number) }]);
    setPick("");
  };
  const save = async (status) => {
    setBusy(true);
    try {
      onSaved(await api("put", `/admin/meetings/${meetingId}/races/${race.number}/analysis`, { selections: sel, recommendation: rec, status }));
      toast.success(status === "published" ? "Tips published" : "Saved as draft");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3" data-testid="approval-editor">
      <div className="flex items-center gap-2">
        <h4 className="font-display font-bold mr-auto">Your final selections</h4>
        <span data-testid="analysis-status" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${analysis.status === "published" ? "bg-emerald-500 text-slate-950" : "bg-slate-700"}`}>{analysis.status}</span>
      </div>
      {sel.map((s, i) => (
        <div key={s.number} className="flex gap-2 items-center" data-testid={`approval-selection-${i + 1}`}>
          <span className="font-mono text-xs w-6">{i + 1}.</span>
          <span className="text-sm font-bold w-40 truncate">{s.number}. {s.horse}{s.price ? <span className="block font-mono text-[10px] font-normal text-amber-300" data-testid={`approval-price-${i + 1}`} title="Price captured when published (used for roughie profit/loss)">${Number(s.price).toFixed(2)}{s.price_source ? ` · ${s.price_source}` : ""}</span> : null}</span>
          <select value={s.role || ""} aria-label="Role" data-testid={`approval-role-${i + 1}`}
            onChange={(e) => setSel(sel.map((x, j) => (j === i ? { ...x, role: e.target.value || null } : e.target.value === "roughie" && x.role === "roughie" ? { ...x, role: null } : x)))}
            className="bg-[#0B101D] border border-white/10 rounded px-1.5 py-1 text-xs">
            <option value="">No role</option><option value="top pick">Top pick</option><option value="danger">Danger</option><option value="roughie">Roughie</option>
          </select>
          <input value={s.note} onChange={(e) => setSel(sel.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
            placeholder="Your comment (shown publicly)" data-testid={`approval-note-${i + 1}`}
            className="flex-1 bg-[#0B101D] border border-white/10 rounded px-2 py-1 text-xs outline-none focus:border-blue-500" />
          <button onClick={() => setSel(sel.filter((_, j) => j !== i))} className="p-1.5 hover:text-rose-400" aria-label="remove"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <select data-testid="approval-add-select" value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 bg-[#0B101D] border border-white/10 rounded px-2 py-1.5 text-xs">
          <option value="">Add a selection…</option>
          {race.runners.map((r) => <option key={r.number} value={r.number}>{r.number}. {r.horse}</option>)}
        </select>
        <button data-testid="approval-add-button" onClick={add} disabled={!pick} className="px-3 rounded bg-white/10 hover:bg-white/20 disabled:opacity-40"><Plus className="w-4 h-4" /></button>
      </div>
      <textarea data-testid="approval-recommendation" rows={4} value={rec} onChange={(e) => setRec(e.target.value)}
        placeholder="Public write-up for this race" className="w-full bg-[#0B101D] border border-white/10 rounded p-2 text-sm outline-none focus:border-blue-500" />
      <div className="flex flex-wrap gap-2">
        <button data-testid="approval-save-draft" onClick={() => save("draft")} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm">
          {analysis.status === "published" ? <><EyeOff className="w-4 h-4" />Unpublish</> : <><Save className="w-4 h-4" />Save draft</>}
        </button>
        <button data-testid="approval-publish" onClick={() => save("published")} disabled={busy || locked || !sel.length} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold disabled:opacity-40">
          <Send className="w-4 h-4" />Approve & publish
        </button>
      </div>
    </div>
  );
};
